import * as service from "../service";
import type { ServiceResponse } from "../types";

/**
 * Create a SetupIntent for the embedded Payment Element, so the business can
 * replace its saved card without ever posting raw card data to our server —
 * Stripe.js collects it and confirms the intent in the browser.
 * Returns the `client_secret` the Payment Element needs; failures carry a
 * human-readable `message` (-40023 no customer, 2 gateway) to show as-is.
 * Backend: POST /api/business/plan/createSetupIntent
 */
export const CreateSetupIntentAPI = async (): Promise<
  ServiceResponse<{ client_secret?: string; message?: string }>
> => {
  try {
    const resp = (await service.call(
      "POST",
      "api/business/plan/createSetupIntent",
      {},
    )) as ServiceResponse<{ client_secret?: string; message?: string }>;
    return resp;
  } catch (err) {
    console.error(`CreateSetupIntentAPI ${err}`);
    return { status: -1, error: err };
  }
};

export type BillingSummaryCard = {
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  status?: string | null;
};

/**
 * Everything the in-page billing panel shows: the plan and its rate snapshot,
 * the card on file, and what is still owed. HKD values are plain 2dp numbers,
 * dates are ISO strings or null.
 */
export type BillingSummaryResult = {
  plan_type?: string;
  status?: string;
  monthly_fee?: number;
  yearly_fee?: number;
  renews_on?: string | null;
  minimum_term_end?: string | null;
  is_promo?: boolean;
  free_trial_end?: string | null;
  card?: BillingSummaryCard | null;
  card_status?: string;
  gateway_configured?: boolean;
  outstanding?: {
    late_fee_hkd?: number;
    shortfall_hkd?: number;
    shortfall_count?: number;
    total_hkd?: number;
    has_outstanding?: boolean;
  } | null;
};

/**
 * Read the billing panel's data — plan, card on file and outstanding balance —
 * without bouncing the business to the Stripe Billing Portal.
 * Backend: GET /api/business/plan/billingSummary
 */
export const GetBillingSummaryAPI = async (): Promise<
  ServiceResponse<BillingSummaryResult>
> => {
  try {
    const resp = (await service.call(
      "GET",
      "api/business/plan/billingSummary",
      {},
    )) as ServiceResponse<BillingSummaryResult>;
    return resp;
  } catch (err) {
    console.error(`GetBillingSummaryAPI ${err}`);
    return { status: -1, error: err };
  }
};
